/**
 * Type adapters to convert Supabase database types to frontend types
 * This allows the existing UI components to work without modification
 */

import type { Tables } from './database.types';
import type { CalendarEvent, Propuesta as FrontendPropuesta, Gestion as FrontendGestion } from '../types';
import { getMunicipalityByName } from './api/municipalities';

// Type guard for valid prioridad values
type Prioridad = 'Alta' | 'Media' | 'Baja';
function isValidPrioridad(value: string | null | undefined): value is Prioridad {
  return value === 'Alta' || value === 'Media' || value === 'Baja';
}

// ⚠️ DEPRECATED: Global mutable state - causes stale data and testing issues
// TODO: Migrate all consumers to use React Context instead
//
// For municipalities data, use: contexts/MunicipalitiesContext.tsx
//   - Provides: useMunicipalities() hook
//   - getMunicipalityName(id: string): string
//   - municipalities: Municipality[]
//   - municipalityNames: string[]
//
// For reuniones data, create: contexts/ReunionesContext.tsx (not yet implemented)
//   - Should provide: getReunionTitle(id: string): string
//
// These module-level Maps are NOT organization-scoped and can retain stale data
// when switching organizations or during testing. They should only be used for
// backward compatibility during migration.
//
// Related: ARCH-MED-001 (Medium severity audit fix)
let municipalitiesMap: Map<string, string> = new Map();
let municipalitiesReverseMap: Map<string, string> = new Map(); // name -> id
let reunionesMap: Map<string, { title: string }> = new Map();

/**
 * Initialize lookup maps for ID -> Name conversions
 * Can be called multiple times safely - will update maps incrementally
 *
 * ⚠️ DEPRECATED: Use React Context instead
 * - For municipalities: use MunicipalitiesContext (contexts/MunicipalitiesContext.tsx)
 * - For reuniones: create ReunionesContext (not yet implemented)
 *
 * This function maintains global mutable state which causes issues:
 * 1. Not organization-scoped - data persists across org switches
 * 2. Difficult to test - shared state between tests
 * 3. No loading/error states - silent failures
 * 4. No re-render triggers - stale data in UI
 *
 * @deprecated Use MunicipalitiesContext.useMunicipalities() instead
 */
export function initializeLookupMaps(data: {
  municipalities?: Array<{ id: string; name: string }>;
  reuniones?: Array<{ id: string; title: string }>;
}) {
  if (data.municipalities && data.municipalities.length > 0) {
    municipalitiesMap = new Map(data.municipalities.map(m => [m.id, m.name]));
    municipalitiesReverseMap = new Map(data.municipalities.map(m => [m.name, m.id]));
  }
  if (data.reuniones && data.reuniones.length > 0) {
    reunionesMap = new Map(data.reuniones.map(r => [r.id, { title: r.title }]));
  }
}

/**
 * Check if municipalities map is initialized
 *
 * ⚠️ DEPRECATED: Use MunicipalitiesContext instead
 *
 * Instead of checking this flag, use the context's loading state:
 * ```ts
 * const { loading } = useMunicipalities();
 * if (loading) return <Spinner />;
 * ```
 *
 * @deprecated Use MunicipalitiesContext.loading instead
 */
export function isMunicipalitiesMapReady(): boolean {
  return municipalitiesMap.size > 0;
}

/**
 * Convert database Reunion to frontend CalendarEvent
 * Accepts either Reunion or ReunionWithCounts
 */
export function reunionToCalendarEvent(reunion: Tables<'reuniones'> & { propuestasCount?: number; gestionesCount?: number }): CalendarEvent {
  const municipalityName = municipalitiesMap.get(reunion.municipality_id) || 'Unknown';

  return {
    id: reunion.id,
    date: reunion.date,
    title: reunion.title,
    type: 'reunion',
    time: reunion.time.slice(0, 5), // HH:MM:SS -> HH:MM
    color: 'bg-[#5D1828]/10 text-[#5D1828] border-[#5D1828]/20',
    municipality: municipalityName,
    organizer: reunion.organizer,
    phone: reunion.organizer_phone || undefined,
    notes: reunion.notes || undefined,
    propuestasCount: reunion.propuestasCount || 0,
    gestionesCount: reunion.gestionesCount || 0,
    // Audit watermark fields
    created_by: reunion.created_by || null,
    created_at: reunion.created_at || null,
    confirmed_by: reunion.confirmed_by || null,
    realized_by: reunion.realized_by || null,
  };
}

/**
 * Convert database Propuesta to frontend Propuesta
 */
export function dbPropuestaToFrontend(propuesta: Tables<'propuestas'>): FrontendPropuesta {
  const municipalityName = municipalitiesMap.get(propuesta.municipality_id) || 'Unknown';
  const reunion = propuesta.reunion_id ? reunionesMap.get(propuesta.reunion_id) : null;

  return {
    id: 0, // Frontend uses number, we'll keep this for compatibility
    dbId: propuesta.id, // Database UUID for updates/deletes
    folio: propuesta.folio,
    proponent: propuesta.proponent ?? '',
    phone: propuesta.phone ?? '',
    municipality: municipalityName,
    date: propuesta.date,
    status: propuesta.status,
    description: propuesta.description ?? '',
    linkedReunionId: undefined, // Deprecated - use linkedReunionDbId instead
    linkedReunionDbId: propuesta.reunion_id || undefined, // Actual UUID for API operations
    linkedReunionTitle: reunion?.title, // May be undefined if reunion not in cache
    prioridad: isValidPrioridad(propuesta.prioridad) ? propuesta.prioridad : undefined,
    numero_beneficiarios: propuesta.numero_beneficiarios ?? undefined,
    organizacion: propuesta.organizacion ?? null, // Map organizacion field
    solicitud: propuesta.solicitud ?? null, // Map solicitud field
    // Audit watermark fields
    created_by: propuesta.created_by || null,
    created_at: propuesta.created_at || null,
    completed_by: propuesta.completed_by || null,
    completed_at: propuesta.completed_at || null,
  };
}

/**
 * Convert database Gestion to frontend Gestion
 */
export function dbGestionToFrontend(gestion: Tables<'gestiones'>): FrontendGestion {
  const municipalityName = municipalitiesMap.get(gestion.municipality_id) || 'Unknown';
  const reunion = gestion.reunion_id ? reunionesMap.get(gestion.reunion_id) : null;

  return {
    id: 0, // Frontend uses number, we'll keep this for compatibility
    dbId: gestion.id, // Database UUID for updates/deletes
    folio: gestion.folio,
    solicitant: gestion.solicitant ?? '',
    phone: gestion.phone ?? '',
    municipality: municipalityName,
    date: gestion.date,
    status: gestion.status,
    description: gestion.description ?? '',
    linkedReunionId: undefined, // Deprecated - use linkedReunionDbId instead
    linkedReunionDbId: gestion.reunion_id || undefined, // Actual UUID for API operations
    linkedReunionTitle: reunion?.title, // May be undefined if reunion not in cache
    prioridad: isValidPrioridad(gestion.prioridad) ? gestion.prioridad : undefined,
    numero_beneficiarios: gestion.numero_beneficiarios ?? undefined,
    organizacion: gestion.organizacion ?? null, // Map organizacion field
    solicitud: gestion.solicitud ?? null, // Map solicitud field
    // Audit watermark fields
    created_by: gestion.created_by || null,
    created_at: gestion.created_at || null,
    completed_by: gestion.completed_by || null,
    completed_at: gestion.completed_at || null,
  };
}

/**
 * Get municipality ID by name (for form submissions)
 * Uses the reverse map for O(1) lookup
 *
 * ⚠️ DEPRECATED: Use MunicipalitiesContext instead
 *
 * Instead of using this global function, use the context data:
 * ```ts
 * const { municipalities } = useMunicipalities();
 * const municipality = municipalities.find(m => m.name === name);
 * const id = municipality?.id || null;
 * ```
 *
 * @deprecated Use MunicipalitiesContext.municipalities instead
 */
export function getMunicipalityId(name: string): string | null {
  if (!name || name === 'Filtrar por municipio' || name === 'Todos los municipios') {
    return null;
  }
  return municipalitiesReverseMap.get(name) || null;
}

/**
 * Get reunion ID by title (for form submissions)
 *
 * ⚠️ DEPRECATED: Create ReunionesContext instead
 *
 * TODO: Create contexts/ReunionesContext.tsx with:
 * ```ts
 * const { reuniones } = useReuniones();
 * const reunion = reuniones.find(r => r.title === title);
 * const id = reunion?.id || null;
 * ```
 *
 * @deprecated Create and use ReunionesContext instead
 */
export function getReunionIdByTitle(title: string): string | null {
  for (const [id, reunion] of reunionesMap.entries()) {
    if (reunion.title === title) return id;
  }
  return null;
}
